import clsx from 'clsx';
import { FC, useMemo } from 'react';
import BarCodedIcon from 'src/assets/icons/BarCodedIcon';
import ContractedIcon from 'src/assets/icons/ContractedIcon';
import FavoritesIcon from 'src/assets/icons/FavoritesIcon';
import FormularyIcon from 'src/assets/icons/FormularyIcon';
import OfflineIcon from 'src/assets/icons/OfflineIcon';
import PictureAvailableIcon from 'src/assets/icons/PictureAvailableIcon';
import SpecialOrderIcon from 'src/assets/icons/SpecialOrderIcon';
import { sortAttributesByOrder } from 'src/sections/catalog_products/table_cell/AttributesFilter';

interface ProductAttributeIconsProps {
  attributes?: string[];
  className?: string;
  height?: string;
  width?: string;
}

type IconComponent = FC<{ height?: string; width?: string }>;

type AttributeIconMap = {
  [key: string]: IconComponent;
};

const iconMap: AttributeIconMap = {
  isContracted: ContractedIcon,
  productInFavoriteFolder: FavoritesIcon,
  isOfflineSupplierItem: OfflineIcon,
  isImageAvailable: PictureAvailableIcon,
  isSpecialOrder: SpecialOrderIcon,
  isFormulary: FormularyIcon,
  hasOldProductId: BarCodedIcon,
};

const attributeDisplayNames: Record<string, string> = {
  isContracted: 'Contracted Item',
  productInFavoriteFolder: 'In Shopping List',
  isOfflineSupplierItem: 'Offline Supplier Item',
  isImageAvailable: 'Picture Available',
  isSpecialOrder: 'Special Order - Item may incur additional shipping charges',
  isFormulary: 'Company Specified',
  hasOldProductId: 'Item Replaced',
};

const ProductAttributeIcons: FC<ProductAttributeIconsProps> = ({
  attributes = [],
  className = '',
  height = '20px',
  width = '20px',
}) => {
  const displayIcons = useMemo(() => {
    if (!attributes || attributes.length === 0) {
      return [];
    }

    const normalizedAttributes = attributes.filter((attr) => attr && iconMap[attr]);

    const uniqueAttributes = Array.from(new Set(normalizedAttributes));

    // Sort attributes according to ATTRIBUTE_ORDER
    const sortedAttributes = sortAttributesByOrder(uniqueAttributes);

    return sortedAttributes.slice(0, 5).map((attr) => ({
      key: attr,
      IconComponent: iconMap[attr],
    }));
  }, [attributes]);

  if (displayIcons.length === 0) {
    return null;
  }

  /**
   * @description Maps the number of attributes to an appropriate gap value
   *              in the provided valid gap-range ( 0.3rem - 0.6rem ).
   *              Uses the following formula:
   *                  output = ((number - in_min) / (in_max - in_min)) * (out_max - out_min) + out_min
   *              where:
   *                  in_min = minimum number of attributes (0)
   *                  in_max = maximum number of attributes (5)
   *                  out_min = minimum gap value (0.3)
   *                  out_max = maximum gap value (0.6)
   *                  number = number of attributes (displayIcons.length)
   *                  output = the calculated gap value
   *
   * NOTE: This is an Immediately Invoked Function Expression (IIFE) ( a self-executing function expression ).
   *
   * @returns the calculated gap value
   */
  const gap = (() => {
    const [inMin, inMax] = [0, 5];
    const [outMin, outMax] = [0.3, 0.6];
    const num: number = displayIcons.length;

    return Math.min(
      parseFloat((((num - inMin) / (inMax - inMin)) * (outMax - outMin) + outMin).toPrecision(2)),
      outMax
    );
  })();

  return (
    <div
      className={clsx('flex items-center', className)}
      style={{
        gap: `${gap}rem`,
      }}
    >
      {displayIcons.map(({ key, IconComponent }) => (
        <div
          key={key}
          data-tooltip-id="global-tooltip"
          data-tooltip-content={attributeDisplayNames[key] || key}
        >
          <IconComponent height={height} width={width} />
        </div>
      ))}
    </div>
  );
};

export default ProductAttributeIcons;
